/**
 * Profile configuration — the tenant's description of its own profile, as it
 * arrives on `GET /public/me`. Which areas and fields exist, and how each field
 * behaves, is decided by the server; the labels and copy for the core fields
 * every tenant has are declared here so they stay typed on the client.
 */

export type ProfileFieldType = 'text' | 'number' | 'boolean' | 'date' | 'select' | 'multiselect';

export interface ProfileFieldOption {
  value: string;
  label: string;
}

/** Per-field behaviour. `reason` explains a read-only field to the resident. */
export interface ProfileFieldConfig {
  key: string;
  visible: boolean;
  editable: boolean;
  required: boolean;
  reason: string | null;
  /**
   * Set on a field the resident changes by verifying the new value rather than
   * by a direct write. Present only when that path is open to them, so the
   * client offers it without deciding the rule itself.
   */
  verified_change?: boolean;
  /**
   * Set on a field whose edit is held for staff approval rather than applied on
   * save. The resident still owns the field and still edits it; the live value
   * only moves once somebody approves. Present only when that is what will
   * happen, so the client says so without deciding the rule.
   */
  requires_approval?: boolean;
  /**
   * Server-described fields only. The core fields every tenant has keep their
   * copy and typing here in the client, so these stay absent for them.
   */
  label?: string;
  type?: ProfileFieldType;
  options?: ProfileFieldOption[];
}

export interface ProfileSectionConfig {
  key: string;
  fields: ProfileFieldConfig[];
}

/** One row on the profile hub. `module_required` gates the area on the tenant's licence. */
export interface ProfileAreaConfig {
  key: string;
  label: string;
  module_required: string | null;
  sections: ProfileSectionConfig[];
}

export interface ProfileConfig {
  areas: ProfileAreaConfig[];
}

/** Values a section's edit form submits: section key -> field key -> value. */
export type ProfileSectionValues = Record<string, Record<string, string>>;

/** A login identifier the resident may change by verifying the new value. */
export type ContactField = 'email' | 'mobile';

/**
 * A change the resident has requested but not confirmed. The value on the
 * profile stays the live one until they do, so both are shown at once.
 */
export interface PendingContactChange {
  field: ContactField;
  new_value: string;
  expires_at: string;
}

/**
 * A supervised change the resident has asked for. It is waiting for staff, or it
 * was turned down and carries the reason. Either way the value on the profile is
 * still the live one, so both are shown at once.
 */
export interface PendingProfileChange {
  section: string;
  field: string;
  new_value: string | null;
  status: 'pending' | 'rejected';
  rejection_reason: string | null;
  requested_at: string;
}

const UNAVAILABLE = 'Profile settings are unavailable right now.';

function readOnly(key: string): ProfileFieldConfig {
  return { key, visible: true, editable: false, required: false, reason: UNAVAILABLE };
}

/**
 * What the profile falls back to when the payload carries no config: today's
 * core fields, read-only. A resident whose tenant config could not be resolved
 * still sees their details rather than an empty screen.
 */
export const FALLBACK_PROFILE_CONFIG: ProfileConfig = {
  areas: [
    {
      key: 'personal',
      label: 'Personal details',
      module_required: null,
      sections: [
        {
          key: 'basics',
          fields: ['first_name', 'last_name', 'mobile', 'email', 'date_of_birth'].map(readOnly),
        },
        { key: 'address', fields: ['line1', 'city', 'postal_code'].map(readOnly) },
      ],
    },
  ],
};

/** Client-owned copy for the core fields, keyed `section.field`. */
export const FIELD_LABELS: Record<string, string> = {
  'basics.first_name': 'First name',
  'basics.last_name': 'Last name',
  'basics.preferred_name': 'Preferred name',
  'basics.mobile': 'Mobile',
  'basics.email': 'Email',
  'basics.date_of_birth': 'Date of birth',
  'basics.gender': 'Gender',
  'basics.id_number': 'ID number',
  'address.line1': 'Street line 1',
  'address.line2': 'Street line 2',
  'address.city': 'City',
  'address.region': 'Region',
  'address.postal_code': 'Postal code',
  'address.country': 'Country',
};

/** Client-owned copy for the section headings the app ships. */
export const SECTION_TITLES: Record<string, string> = {
  basics: 'Basics',
  address: 'Address',
};

export function fieldLabel(sectionKey: string, fieldKey: string): string {
  return FIELD_LABELS[`${sectionKey}.${fieldKey}`] ?? fieldKey;
}

export function visibleFields(section: ProfileSectionConfig): ProfileFieldConfig[] {
  return section.fields.filter((field) => field.visible);
}
